/** Anything a person-picker can search: users, recruiters, contacts. */
export interface NameAndEmail {
  name: string;
  email: string;
}

const COMBINING_MARKS = /\p{M}/gu;
const GERMAN_DIGRAPHS: readonly (readonly [RegExp, string])[] = [
  [/ae/g, "a"],
  [/oe/g, "o"],
  [/ue/g, "u"],
];

/** Lower-case, ß → ss, diacritics dropped, and ae/oe/ue folded to a/o/u on
 * both sides, so "koenemann", "konemann" and "Könemann" all compare equal. */
export function foldForSearch(text: string): string {
  let folded = text.toLowerCase().replace(/ß/g, "ss").normalize("NFD").replace(COMBINING_MARKS, "");
  for (const [digraph, vowel] of GERMAN_DIGRAPHS) folded = folded.replace(digraph, vowel);
  return folded;
}

/** Where a match was found; lower ranks first. */
const Tier = { NamePrefix: 0, WordPrefix: 1, NameContains: 2, EmailOnly: 3 } as const;
type Tier = (typeof Tier)[keyof typeof Tier];

function tierOf(name: string, email: string, query: string, words: string[]): Tier | null {
  if (!words.every((w) => name.includes(w) || email.includes(w))) return null;
  if (name.startsWith(query)) return Tier.NamePrefix;
  const nameWords = name.split(/\s+/);
  if (words.every((w) => nameWords.some((nw) => nw.startsWith(w)))) return Tier.WordPrefix;
  if (words.every((w) => name.includes(w))) return Tier.NameContains;
  return Tier.EmailOnly;
}

const byNameThenEmail = (a: NameAndEmail, b: NameAndEmail): number =>
  a.name.localeCompare(b.name, "de") || a.email.localeCompare(b.email, "de");

/** Type-ahead search over name and email (issue #12). Every word of the
 * query must appear in the name or email, in any order, ignoring case,
 * diacritics and German spelling variants. Results rank by where they
 * matched (name prefix → word prefix → name substring → email only), then
 * alphabetically. An empty query returns everyone, alphabetically. */
export function searchByNameAndEmail<T extends NameAndEmail>(
  items: readonly T[],
  query: string,
): T[] {
  const folded = foldForSearch(query.trim());
  if (!folded) return [...items].sort(byNameThenEmail);
  const words = folded.split(/\s+/);
  return items
    .map((item) => ({
      item,
      tier: tierOf(foldForSearch(item.name), foldForSearch(item.email), folded, words),
    }))
    .filter((m): m is { item: T; tier: Tier } => m.tier !== null)
    .sort((a, b) => a.tier - b.tier || byNameThenEmail(a.item, b.item))
    .map((m) => m.item);
}
